import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { History, MoreHorizontal, PanelRightClose, Plus, X } from 'lucide-react';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeIconButton } from '@/components/ui/tee-icon-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAssistant } from '@/contexts/AssistantContext';
import { useCanSeeTickets } from '@/hooks/useSupportTickets';
import { supportAssistantService } from '@/services/support-assistant.service';
import { cn } from '@/utils/cn';
import type { AssistantSendOptions } from '@/types/support-assistant';
import { AssistantChat } from './AssistantChat';
import { AssistantContextRow } from './AssistantContextRow';
import { AssistantMobileMenu } from './AssistantMobileMenu';
import { AssistantRecentList } from './AssistantRecentList';
import { WhyGopherNote } from './WhyGopherNote';

export interface AssistantPanelProps {
  mode: 'desktop' | 'mobile';
  closeAction: 'collapse' | 'close';
  onCloseAction: () => void;
  className?: string;
}

/** Shared assistant panel; placement and dialog semantics belong to the host. */
export function AssistantPanel({
  mode,
  closeAction,
  onCloseAction,
  className,
}: AssistantPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canSeeTickets = useCanSeeTickets();
  const {
    isOpen,
    view,
    activeSessionId,
    pinned,
    effectiveContext,
    showHistory,
    showChat,
    selectSession,
    startNewConversation,
    pin,
    unpin,
    notifyActivity,
  } = useAssistant();
  const sendRef = useRef<((text: string, options?: AssistantSendOptions) => void) | null>(null);
  const pendingEscalate = useRef(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const session = useQuery({
    queryKey: ['assistant-session', activeSessionId],
    queryFn: () => supportAssistantService.getSession(activeSessionId as string),
    enabled: !!activeSessionId,
    // 30s while collapsed/closed (T-389-66), 10s while open.
    refetchInterval: activeSessionId ? (isOpen ? 10000 : 30000) : false,
  });
  const openTicket = session.data?.session.openTicket;
  const hasPendingAction = session.data?.session.hasPendingAction;

  const hqReplyCount = (session.data?.messages ?? []).filter((m) => m.hqReply).length;
  const activityKey = session.data
    ? `${openTicket?.id ?? ''}|${hasPendingAction ? '1' : '0'}|${hqReplyCount}`
    : null;
  const prevActivityKey = useRef<string | null>(null);
  useEffect(() => {
    if (activityKey === null) return;
    const prev = prevActivityKey.current;
    prevActivityKey.current = activityKey;
    if (prev !== null && prev !== activityKey && !isOpen) notifyActivity();
  }, [activityKey, isOpen, notifyActivity]);

  // Chat stays mounted (hidden) in the history view so streams are not
  // aborted; an escalate requested from history is queued and flushed once the
  // chat view is shown again.
  useEffect(() => {
    if (view !== 'chat') return;
    if (pendingEscalate.current && sendRef.current) {
      pendingEscalate.current = false;
      sendRef.current(t('assistant.escalateRequest'), { intent: 'escalate' });
    }
  }, [view, t]);

  const escalate = () => {
    if (view === 'chat' && sendRef.current) {
      sendRef.current(t('assistant.escalateRequest'), { intent: 'escalate' });
      return;
    }
    pendingEscalate.current = true;
    showChat();
  };

  const label = (pinned ?? effectiveContext)?.label;
  const toggleHistory = () => (view === 'history' ? showChat() : showHistory());
  const isDesktop = mode === 'desktop';

  const closeButton =
    closeAction === 'collapse' ? (
      <TeeIconButton
        aria-label={t('assistant.panel.collapse')}
        icon={<PanelRightClose size={18} />}
        onClick={onCloseAction}
      />
    ) : (
      <TeeIconButton
        aria-label={t('assistant.panel.close')}
        className={isDesktop ? undefined : 'h-11 w-11'}
        icon={<X size={18} />}
        onClick={onCloseAction}
      />
    );

  return (
    <div className={cn('flex h-full flex-col bg-[var(--background-page-content)]', className)}>
      {isDesktop ? (
        <div className="flex h-[44px] shrink-0 items-center justify-between border-b border-[var(--border-default)] pl-[18px] pr-2">
          <h2 className="font-[Urbanist] text-[15px] font-bold text-[var(--text-content-title)]">
            {t('assistant.panel.title')}
          </h2>
          <div className="flex items-center gap-1">
            <TeeIconButton
              aria-label={t('assistant.panel.newConversation')}
              icon={<Plus size={18} />}
              onClick={() => void startNewConversation()}
            />
            <TeeIconButton
              aria-label={t('assistant.panel.history')}
              icon={<History size={18} />}
              onClick={toggleHistory}
            />
            <Popover open={moreOpen} onOpenChange={setMoreOpen}>
              <PopoverTrigger asChild>
                <TeeIconButton
                  aria-label={t('assistant.panel.more')}
                  icon={<MoreHorizontal size={18} />}
                />
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-[240px] rounded-[10px] border-0 bg-[var(--background-surface-floating)] p-1 shadow-[var(--shadow-popover)]"
              >
                <TeeButton
                  variant="ghost"
                  size="s"
                  className="w-full justify-start font-semibold text-[var(--color-forest)]"
                  onClick={() => {
                    setMoreOpen(false);
                    escalate();
                  }}
                  text={t('assistant.composer.escalate')}
                />
                <WhyGopherNote />
              </PopoverContent>
            </Popover>
            {closeButton}
          </div>
        </div>
      ) : (
        <div className="flex h-[48px] shrink-0 items-center justify-between border-b border-[var(--border-default)] pl-4 pr-1">
          <div className="min-w-0">
            <h2 className="font-[Urbanist] text-[17px] font-bold leading-tight text-[var(--text-content-title)]">
              {t('assistant.panel.title')}
            </h2>
            <p className="truncate text-[12px] leading-tight text-[var(--text-content-sub-title)]">
              {label ? (
                <>
                  {t('assistant.context.mobileFollowing')}
                  {' · '}
                  <strong className="font-semibold text-[var(--text-content-title)]">
                    {label}
                  </strong>
                </>
              ) : (
                t('assistant.context.none')
              )}
            </p>
          </div>
          <div className="flex items-center">
            <AssistantMobileMenu
              onNewConversation={() => void startNewConversation()}
              onHistory={showHistory}
              onTogglePin={pinned ? unpin : pin}
              pinLabel={
                label
                  ? pinned
                    ? t('assistant.context.unpinFrom', { entity: label })
                    : t('assistant.context.pinTo', { entity: label })
                  : undefined
              }
              onEscalate={escalate}
            />
            {closeButton}
          </div>
        </div>
      )}

      {isDesktop && <AssistantContextRow />}

      <div className="flex min-h-0 flex-1 flex-col">
        {view === 'history' && (
          <div className="flex flex-col gap-3 overflow-y-auto p-4">
            <TeeButton
              variant="tertiary"
              size="s"
              className="self-start"
              onClick={showChat}
              text={t('assistant.panel.backToChat')}
            />
            <AssistantRecentList
              limit={20}
              heading={t('assistant.panel.historyHeading')}
              onSelect={(id) => {
                selectSession(id);
                showChat();
              }}
            />
          </div>
        )}
        <div className={cn('min-h-0 flex-1 flex-col', view === 'history' ? 'hidden' : 'flex')}>
          <AssistantChat
            sessionId={activeSessionId}
            messageScope={effectiveContext ?? null}
            mobile={mode === 'mobile'}
            showEscalate
            escalatedTicketNumber={openTicket?.ticketNumber}
            onSelectSession={selectSession}
            canSeeTickets={canSeeTickets}
            onViewTicket={(id) => navigate(`/support/tickets?ticket=${encodeURIComponent(id)}`)}
            compact
            renderFooter={(send) => {
              sendRef.current = send;
              return null;
            }}
          />
        </div>
      </div>
    </div>
  );
}
